/**
 * @fileoverview Translation keys for the Capabilities screen's permission, group and usage labels.
 * @description Permissions, groups and the app actions a permission controls are identified by
 * stable codes (USER.INVITE, SALES_CRM, users.addHead), and their plain-language names live in the
 * locale files under `capabilities.permissions.*`, `capabilities.groups.*` and
 * `capabilities.controls.*`. These helpers turn a code into its key, so a permission engineering
 * adds later only needs a locale entry, and falls back to the name in the permission table until
 * then.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import {
  CAPABILITY_GROUP_ORDER,
  PERMISSION_CONTROLS,
} from '@/features/capabilities/constants/capability-controls.constants'

/**
 * @description Turns a code made of words split by dots, underscores or dashes into camelCase.
 * @param {string} code - The code, e.g. "USER_MANAGEMENT.TEAM.READ" or "users.addHead".
 * @returns {string} The camelCase form, e.g. "userManagementTeamRead" or "usersAddHead".
 */
function toCamelCase(code: string): string {
  const words = code.split(/[._-]+/).filter(Boolean)

  return words
    .map((word, index) => {
      // Mixed-case words (addHead) already carry their own humps: keep them, just lower the first.
      const normalized = word === word.toUpperCase() ? word.toLowerCase() : word
      return index === 0
        ? normalized.charAt(0).toLowerCase() + normalized.slice(1)
        : normalized.charAt(0).toUpperCase() + normalized.slice(1)
    })
    .join('')
}

/**
 * @description The translation key for a permission's plain-language name.
 * @param {string} permissionCode - The permission code, e.g. "USER.INVITE".
 * @returns {string} The key, e.g. "capabilities.permissions.userInvite".
 */
export function permissionNameKey(permissionCode: string): string {
  return `capabilities.permissions.${toCamelCase(permissionCode)}`
}

/**
 * @description The translation key for a group (module card) title.
 * @param {string} groupKey - The group key, e.g. "SALES_CRM".
 * @returns {string} The key, e.g. "capabilities.groups.salesCrm".
 */
export function groupTitleKey(groupKey: string): string {
  return `capabilities.groups.${toCamelCase(groupKey)}`
}

/**
 * @description The translation key for an app action's label (e.g. "Dashboard · View").
 * @param {string} action - The action code, e.g. "dash.view".
 * @returns {string} The key, e.g. "capabilities.controls.dashView".
 */
export function actionLabelKey(action: string): string {
  return `capabilities.controls.${toCamelCase(action)}`
}

/**
 * @description The app actions a permission controls in the Sales app.
 * @param {string} permissionCode - The permission code.
 * @returns {readonly string[]} The action codes, empty when the permission isn't used by this app.
 */
export function getPermissionControls(permissionCode: string): readonly string[] {
  return PERMISSION_CONTROLS[permissionCode] ?? []
}

/**
 * @description Sorts group keys the way the page lists them: known groups in their set order,
 * then any new ones by name.
 * @param {string} left - One group key.
 * @param {string} right - Another group key.
 * @returns {number} Negative, zero or positive, as for Array.prototype.sort.
 */
export function compareGroupKeys(left: string, right: string): number {
  const leftIndex = CAPABILITY_GROUP_ORDER.indexOf(left)
  const rightIndex = CAPABILITY_GROUP_ORDER.indexOf(right)
  const leftRank = leftIndex === -1 ? CAPABILITY_GROUP_ORDER.length : leftIndex
  const rightRank = rightIndex === -1 ? CAPABILITY_GROUP_ORDER.length : rightIndex

  return leftRank - rightRank || left.localeCompare(right)
}
